/**
 * Layer Manager Module
 * Handles layer state, operations, and lifecycle
 */

import { addGeoJSONToMap, removeLayerFromMap, setLayerVisibility, setupLayerEditHover, getMap } from '../map.js';
import { displayAttributeTable, createScrollHandler, clearTable } from './tableManager.js';
import { createLayerTab, switchToLayerTab, removeLayerTab, clearAllTabs } from './tabManager.js';
import { updateShapefile } from '../db.js';

// State
let layers = [];
let nextLayerId = 1;
let currentLayer = null; // Track the currently active layer
let currentGeojson = null;
let currentMetadata = null;
let currentFeatures = [];
let tableScrollContainer = null;
let currentScrollHandler = null;

// Layer colors
const LAYER_COLORS = ['#667eea', '#f093fb', '#4facfe', '#43e97b', '#fa709a'];

export function initializeLayerManager(scrollContainer) {
    tableScrollContainer = scrollContainer;
}

function getGeometryPriority(metadata) {
    const { geometryTypes } = metadata;
    if (!geometryTypes || geometryTypes.length === 0) return 2;

    const primaryType = geometryTypes[0];
    if (primaryType.includes('Polygon')) return 0; // Bottom
    if (primaryType.includes('Line')) return 1; // Middle
    if (primaryType.includes('Point')) return 2; // Top
    return 2;
}

function findInsertionIndex(priority) {
    // Find the first layer with a higher priority (should be rendered on top)
    for (let i = 0; i < layers.length; i++) {
        const layerPriority = getGeometryPriority(layers[i].metadata);
        if (layerPriority > priority) {
            return i;
        }
    }
    // If no layer has higher priority, add at the end (top)
    return layers.length;
}

export function addLayer(layerData, onFeatureClick, options = {}) {
    const layerId = `layer-${nextLayerId++}`;
    const color = LAYER_COLORS[(layers.length) % LAYER_COLORS.length];

    const layer = {
        id: layerId,
        name: layerData.name,
        geojson: layerData.geojson,
        metadata: layerData.metadata,
        featureCount: layerData.featureCount,
        visible: true,
        color: color,
        dbId: layerData.dbId || null, // Store database ID if provided
        hasEdits: false // Track if layer has been edited
    };

    // Insert layer at the correct position based on geometry type
    // Polygons at bottom, lines in middle, points at top
    const priority = getGeometryPriority(layer.metadata);
    const insertIndex = findInsertionIndex(priority);
    layers.splice(insertIndex, 0, layer);

    // Add to map
    addGeoJSONToMap(layer.geojson, {
        layerId: layerId,
        layerName: layer.name,
        color: color,
        metadata: layer.metadata, // Pass metadata so empty layers know their geometry type
        skipFitBounds: options.skipFitBounds, // Pass through skipFitBounds option
        onFeatureClick: (index) => {
            // Ensure we're viewing this layer's table
            switchToLayerTab(layerId);

            // Call the feature click handler
            if (onFeatureClick) {
                onFeatureClick(index, layer);
            }
        }
    });

    // Set up hover handlers for edit mode selection
    setupLayerEditHover(layerId);
    setupLayerEditHover(`${layerId}-fill`); // For polygon fill layers

    // Reorder map layers to match the layer array order
    // This ensures proper stacking (Polygons → Lines → Points)
    updateMapLayerOrder();

    // Create tab for this layer
    createLayerTab(layer);

    // Switch to this layer's tab (makes it the active/focused tab)
    switchToLayerTab(layerId);

    return layerId;
}

/**
 * Update the map layer order to match the layers array
 * First layer in array = bottom of map, last layer = top
 */
function updateMapLayerOrder() {
    const map = getMap();
    if (!map) return;

    // Get the first base layer ID (the layer to position user layers after)
    let firstBaseLayerId = 'carto-dark';
    if (!map.getLayer(firstBaseLayerId)) {
        firstBaseLayerId = 'osm';
    }
    if (!map.getLayer(firstBaseLayerId)) {
        firstBaseLayerId = 'dark-overlay';
    }

    // Rebuild the layer stack from bottom to top
    for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        try {
            if (map.getLayer(layer.id)) {
                if (i === 0) {
                    // First layer goes right after the base layer
                    const nextUserLayer = layers[i + 1];
                    if (nextUserLayer && map.getLayer(nextUserLayer.id)) {
                        map.moveLayer(layer.id, nextUserLayer.id);
                    } else {
                        // Only layer, move to top
                        map.moveLayer(layer.id);
                    }
                } else {
                    // Move to the top (above all other user layers)
                    map.moveLayer(layer.id);
                }
            }
        } catch (e) {
            console.warn('Could not order layer:', layer.id, e);
        }
    }
}

/**
 * Remove a layer from map and layer list
 * @param {string} layerId - Layer ID to remove
 */
export function removeLayer(layerId) {
    const index = layers.findIndex(l => l.id === layerId);
    if (index !== -1) {
        // Clear current layer reference if removing the current layer
        if (currentLayer && currentLayer.id === layerId) {
            currentLayer = null;
            currentGeojson = null;
            currentMetadata = null;
            currentFeatures = [];
        }

        // Remove layer tab first (before removing from array)
        removeLayerTab(layerId, layers);

        layers.splice(index, 1);
        removeLayerFromMap(layerId);

        return true;
    }
    return false;
}

/**
 * Remove all layers
 */
export function removeAllLayers() {
    layers.forEach(layer => {
        removeLayerFromMap(layer.id);
    });
    layers = [];
    nextLayerId = 1;
    currentLayer = null;
    currentGeojson = null;
    currentMetadata = null;
    currentFeatures = [];

    // Clear all tabs and table
    clearAllTabs();
    clearTable();
}

/**
 * Toggle layer visibility
 * @param {string} layerId - Layer ID
 */
export function toggleLayerVisibility(layerId) {
    const layer = layers.find(l => l.id === layerId);
    if (layer) {
        layer.visible = !layer.visible;
        setLayerVisibility(layerId, layer.visible);
    }
}

/**
 * Get a layer by ID
 * @param {string} layerId - Layer ID
 * @returns {Object|null} Layer object or null
 */
export function getLayer(layerId) {
    return layers.find(l => l.id === layerId) || null;
}

/**
 * Get all layers
 * @returns {Array} Array of all layers
 */
export function getAllLayers() {
    return [...layers];
}

/**
 * Get layer count
 * @returns {number} Number of layers
 */
export function getLayerCount() {
    return layers.length;
}

/**
 * Reorder layers - move draggedLayerId to the position of targetLayerId
 * @param {string} draggedLayerId - ID of the layer being dragged
 * @param {string} targetLayerId - ID of the layer to drop onto
 */
export function reorderLayers(draggedLayerId, targetLayerId) {
    const draggedIndex = layers.findIndex(l => l.id === draggedLayerId);
    const targetIndex = layers.findIndex(l => l.id === targetLayerId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Remove the dragged layer from its current position
    const [draggedLayer] = layers.splice(draggedIndex, 1);

    // Insert it at the target position
    layers.splice(targetIndex, 0, draggedLayer);

    // Update the map to match the new layer order
    updateMapLayerOrder();
}

/**
 * Set current layer data (used when switching tabs)
 * @param {string} layerId - Layer ID
 * @param {Function} onRowClick - Callback when table row is clicked
 */
export function setCurrentLayer(layerId, onRowClick) {
    const layer = getLayer(layerId);
    if (!layer) return;

    // Update current state
    currentLayer = layer; // Store reference to current layer
    currentGeojson = layer.geojson;
    currentFeatures = layer.geojson.features;
    currentMetadata = layer.metadata;

    // Remove old scroll handler if exists
    if (currentScrollHandler && tableScrollContainer) {
        tableScrollContainer.removeEventListener('scroll', currentScrollHandler);
    }

    // Display the layer's attribute table
    displayAttributeTable(layer.geojson, onRowClick, layer.metadata);

    // Create and attach new scroll handler
    if (tableScrollContainer) {
        currentScrollHandler = createScrollHandler(tableScrollContainer);
        tableScrollContainer.addEventListener('scroll', currentScrollHandler);
    }
}

/**
 * Get current layer data
 * @returns {Object} Current layer data
 */
export function getCurrentLayerData() {
    return {
        geojson: currentGeojson,
        metadata: currentMetadata,
        features: currentFeatures
    };
}

/**
 * Get feature by index
 * @param {number} index - Feature index
 * @returns {Object|null} Feature or null
 */
export function getFeature(index) {
    if (currentFeatures && index >= 0 && index < currentFeatures.length) {
        return currentFeatures[index];
    }
    return null;
}

/**
 * Handle feature data updates from table edits
 */
document.addEventListener('featureDataUpdated', async (event) => {
    const { featureIndex, propertyKey, newValue } = event.detail;

    // Update the current layer's GeoJSON
    if (currentLayer && currentLayer.geojson && currentLayer.geojson.features && currentLayer.geojson.features[featureIndex]) {
        currentLayer.geojson.features[featureIndex].properties[propertyKey] = newValue;

        // Also update currentGeojson reference
        if (currentGeojson && currentGeojson.features && currentGeojson.features[featureIndex]) {
            currentGeojson.features[featureIndex].properties[propertyKey] = newValue;
        }

        // Mark layer as edited
        currentLayer.hasEdits = true;
        console.log(`Layer ${currentLayer.id} marked as edited (data update)`);

        // Update the map source to reflect the property change
        const { updateLayerGeometry } = await import('../map.js');
        updateLayerGeometry(currentLayer.id, currentLayer.geojson);
        console.log(`Updated map source for layer ${currentLayer.id}`);

        // Dispatch event to update UI
        document.dispatchEvent(new CustomEvent('layerEdited', { detail: { layerId: currentLayer.id } }));
        console.log(`Dispatched layerEdited event for ${currentLayer.id}`);

        // Save to IndexedDB if this layer has a database ID
        if (currentLayer.dbId) {
            try {
                await updateShapefile(currentLayer.dbId, {
                    geojson: currentLayer.geojson,
                    metadata: currentLayer.metadata,
                    name: currentLayer.name
                });
                console.log(`Updated feature ${featureIndex}, ${propertyKey} = ${newValue} (saved to IndexedDB)`);
            } catch (error) {
                console.error('Failed to save changes to IndexedDB:', error);
            }
        } else {
            console.log(`Updated feature ${featureIndex}, ${propertyKey} = ${newValue} (not saved - layer not from database)`);
        }
    }
});

/**
 * Handle layer data updates (e.g., field additions)
 */
document.addEventListener('layerDataUpdated', async (event) => {
    const { action, fieldName, fieldType, defaultValue, fieldTypes } = event.detail;

    if (action === 'fieldAdded' && currentLayer) {
        // Update layer metadata with field types
        if (!currentLayer.metadata) {
            currentLayer.metadata = {};
        }
        currentLayer.metadata.fieldTypes = fieldTypes;

        // Mark layer as edited
        currentLayer.hasEdits = true;
        console.log(`Layer ${currentLayer.id} marked as edited (field added: ${fieldName})`);

        // Update the map source to reflect the changes
        const { updateLayerGeometry } = await import('../map.js');
        updateLayerGeometry(currentLayer.id, currentLayer.geojson);
        console.log(`Updated map source for layer ${currentLayer.id}`);

        // Dispatch event to update UI
        document.dispatchEvent(new CustomEvent('layerEdited', { detail: { layerId: currentLayer.id } }));
        console.log(`Dispatched layerEdited event for ${currentLayer.id}`);

        // Save to IndexedDB if this layer has a database ID
        if (currentLayer.dbId) {
            try {
                await updateShapefile(currentLayer.dbId, {
                    geojson: currentLayer.geojson,
                    metadata: currentLayer.metadata,
                    name: currentLayer.name
                });
                console.log(`Added field "${fieldName}" (${fieldType}) to layer (saved to IndexedDB)`);
            } catch (error) {
                console.error('Failed to save changes to IndexedDB:', error);
            }
        } else {
            console.log(`Added field "${fieldName}" (${fieldType}) to layer (not saved - layer not from database)`);
        }
    }
});

/**
 * Handle geometry creation from draw control
 */
document.addEventListener('geometryCreated', async (event) => {
    const { layerId, geometry, properties } = event.detail;

    // Find the layer by ID
    const layer = layers.find(l => l.id === layerId);
    if (!layer) {
        console.error('Layer not found for geometry creation:', layerId);
        return;
    }

    // Initialize properties with default fields from layer metadata
    const initialProperties = { ...(properties || {}) };

    // Add default fields if they exist in metadata and aren't already set
    if (layer.metadata && layer.metadata.defaultFields) {
        Object.entries(layer.metadata.defaultFields).forEach(([key, value]) => {
            if (!(key in initialProperties)) {
                initialProperties[key] = value;
            }
        });
    }

    // Create new feature
    const newFeature = {
        type: 'Feature',
        geometry: geometry,
        properties: initialProperties
    };

    // Add the feature to the layer's GeoJSON
    layer.geojson.features.push(newFeature);

    // Re-index all features
    layer.geojson.features.forEach((feature, index) => {
        feature.properties.__featureIndex = index;
    });

    // Update feature count
    layer.featureCount = layer.geojson.features.length;

    // Mark layer as edited
    layer.hasEdits = true;
    console.log(`Layer ${layer.id} marked as edited (new feature added)`);

    // Dispatch event to update UI
    document.dispatchEvent(new CustomEvent('layerEdited', { detail: { layerId: layer.id } }));

    // Update currentLayer and currentGeojson if this is the active layer
    if (currentLayer && currentLayer.id === layerId) {
        currentLayer = layer;
        currentGeojson = layer.geojson;
    }

    // Update the map layer to reflect changes
    const { updateLayerGeometry } = await import('../map.js');
    updateLayerGeometry(layer.id, layer.geojson);

    // Save to IndexedDB if this layer has a database ID
    if (layer.dbId) {
        try {
            await updateShapefile(layer.dbId, {
                geojson: layer.geojson,
                metadata: layer.metadata,
                name: layer.name
            });
            console.log(`Added new feature (saved to IndexedDB)`);
        } catch (error) {
            console.error('Failed to save new feature to IndexedDB:', error);
        }
    }

    // Refresh the table to show the new feature (if this is the current layer)
    if (currentLayer && currentLayer.id === layerId) {
        const { displayAttributeTable } = await import('./tableManager.js');
        displayAttributeTable(layer.geojson, null, layer.metadata);
        console.log('Refreshed attribute table with new feature');
    }

    // Update the legend to show new feature count
    const updateLegendEvent = new CustomEvent('updateLegend');
    document.dispatchEvent(updateLegendEvent);

    // Show success message
    const statusEvent = new CustomEvent('showStatus', {
        detail: {
            message: `Feature added successfully! Total: ${layer.featureCount}`,
            type: 'success'
        }
    });
    document.dispatchEvent(statusEvent);

    console.log(`Created new feature in layer ${layer.id}. Total features: ${layer.featureCount}`);
});

/**
 * Handle geometry updates from draw control
 */
document.addEventListener('geometryUpdated', async (event) => {
    const { featureIndex, geometry } = event.detail;

    // Update the current layer's GeoJSON
    if (currentLayer && currentLayer.geojson && currentLayer.geojson.features && currentLayer.geojson.features[featureIndex]) {
        currentLayer.geojson.features[featureIndex].geometry = geometry;

        // Also update currentGeojson reference
        if (currentGeojson && currentGeojson.features && currentGeojson.features[featureIndex]) {
            currentGeojson.features[featureIndex].geometry = geometry;
        }

        // Mark layer as edited
        currentLayer.hasEdits = true;
        console.log(`Layer ${currentLayer.id} marked as edited (geometry update)`);

        // Dispatch event to update UI
        document.dispatchEvent(new CustomEvent('layerEdited', { detail: { layerId: currentLayer.id } }));
        console.log(`Dispatched layerEdited event for ${currentLayer.id}`);

        // Update the map layer to reflect changes
        const { updateLayerGeometry } = await import('../map.js');
        updateLayerGeometry(currentLayer.id, currentLayer.geojson);

        // Save to IndexedDB if this layer has a database ID
        if (currentLayer.dbId) {
            try {
                await updateShapefile(currentLayer.dbId, {
                    geojson: currentLayer.geojson,
                    metadata: currentLayer.metadata,
                    name: currentLayer.name
                });
                console.log(`Updated feature ${featureIndex} geometry (saved to IndexedDB)`);
            } catch (error) {
                console.error('Failed to save geometry changes to IndexedDB:', error);
            }
        } else {
            console.log(`Updated feature ${featureIndex} geometry (not saved - layer not from database)`);
        }
    }
});

/**
 * Handle geometry deletion from draw control
 */
document.addEventListener('geometryDeleted', async (event) => {
    const { featureIndex } = event.detail;

    // Remove the feature from the current layer's GeoJSON
    if (currentLayer && currentLayer.geojson && currentLayer.geojson.features && currentLayer.geojson.features[featureIndex]) {
        // Mark as deleted or remove (we'll remove it)
        currentLayer.geojson.features.splice(featureIndex, 1);

        // Re-index remaining features
        currentLayer.geojson.features.forEach((feature, index) => {
            feature.properties.__featureIndex = index;
        });

        // Also update currentGeojson reference
        if (currentGeojson && currentGeojson.features) {
            currentGeojson.features.splice(featureIndex, 1);
            currentGeojson.features.forEach((feature, index) => {
                feature.properties.__featureIndex = index;
            });
        }

        // Mark layer as edited
        currentLayer.hasEdits = true;
        console.log(`Layer ${currentLayer.id} marked as edited (geometry deletion)`);

        // Dispatch event to update UI
        document.dispatchEvent(new CustomEvent('layerEdited', { detail: { layerId: currentLayer.id } }));
        console.log(`Dispatched layerEdited event for ${currentLayer.id}`);

        // Update the map layer to reflect changes
        const { updateLayerGeometry } = await import('../map.js');
        updateLayerGeometry(currentLayer.id, currentLayer.geojson);

        // Refresh the attribute table
        const { displayAttributeTable } = await import('./tableManager.js');
        displayAttributeTable(currentLayer.geojson, null, currentLayer.metadata);

        // Save to IndexedDB if this layer has a database ID
        if (currentLayer.dbId) {
            try {
                await updateShapefile(currentLayer.dbId, {
                    geojson: currentLayer.geojson,
                    metadata: currentLayer.metadata,
                    name: currentLayer.name
                });
                console.log(`Deleted feature ${featureIndex} (saved to IndexedDB)`);
            } catch (error) {
                console.error('Failed to save deletion to IndexedDB:', error);
            }
        } else {
            console.log(`Deleted feature ${featureIndex} (not saved - layer not from database)`);
        }
    }
});

/**
 * Get current layer
 * @returns {Object|null} Current layer or null
 */
export function getCurrentLayer() {
    return currentLayer;
}
